import { useState, useCallback, useEffect } from "react";
import { ideasService } from "../service/api";
import { useToast } from "@/core/hooks/useToast";

export const useIdeaCommentsViewModel = (ideaId: string | undefined) => {
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const { showError } = useToast();

  const loadComments = useCallback(async () => {
    if (!ideaId) return;

    try {
      setLoading(true);
      const commentsData = await ideasService.getIdeaComments(ideaId);
      setComments(commentsData);
    } catch (error: any) {
      console.error("Failed to load comments:", error);
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const createComment = useCallback(async () => {
    if (!ideaId || !newComment.trim()) return;

    try {
      setCreateLoading(true);
      await ideasService.createIdeaComment({
        idea_id: ideaId,
        body: newComment.trim(),
      });
      setNewComment("");
      await loadComments();
    } catch (error: any) {
      console.error("Failed to create comment:", error);
      showError(error?.error || "Failed to create comment");
    } finally {
      setCreateLoading(false);
    }
  }, [ideaId, newComment, loadComments, showError]);

  const createReply = useCallback(
    async (commentId: string) => {
      if (!ideaId || !replyText.trim()) return;

      try {
        setCreateLoading(true);
        await ideasService.createIdeaComment({
          idea_id: ideaId,
          body: replyText.trim(),
          reply_to_cid: commentId,
        });
        setReplyText("");
        setReplyingTo(null);
        await loadComments();
      } catch (error: any) {
        console.error("Failed to create reply:", error);
        showError(error?.error || "Failed to create reply");
      } finally {
        setCreateLoading(false);
      }
    },
    [ideaId, replyText, loadComments, showError]
  );

  const handleReplyClick = useCallback(
    (commentId: string) => {
      setReplyingTo(replyingTo === commentId ? null : commentId);
      setReplyText("");
    },
    [replyingTo]
  );

  return {
    comments,
    loading,
    newComment,
    setNewComment,
    createLoading,
    replyingTo,
    replyText,
    setReplyText,
    createComment,
    createReply,
    handleReplyClick,
  };
};
